import { useEffect, useState } from 'react';
import {
  Stack,
  Typography,
  Alert,
  AlertTitle,
  Toolbar,
  Divider,
  Button,
  IconButton,
  Box,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { deleteChat } from '@/actions/chat';
import { useUser } from '@/providers/UserContext';
import ChatComponent from './Chat';
import LoadingMask from '@/components/shared/LoadingMask';

import { useApi } from '@/providers/ApiContext';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { useNavigate, useParams } from 'react-router';
import { getChat } from '@/actions/chat';
import ChatDeleteDialog from '@/components/shared/ChatDeleteDialog';
import type { Chat, EntityId } from '@/types/apiTypes';

function ExternalAudit(): React.JSX.Element {
  const user = useUser();
  const { audit_id } = useParams<{ audit_id: string }>();
  const navigate = useNavigate();
  const api = useApi();
  const [chat, setChat] = useState<Chat | null>(null);
  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);

  useEffect(() => {
    if (user?.entity_id && !chat && audit_id && api) {
      getChat(api, audit_id)
        .then((data) => {
          setChat(data);
        })
        .catch((error) => {
          console.log(error);
          navigate('/quartermaster/externalAudit');
        });
    }
  }, [user?.entity_id, audit_id, chat, api, navigate]);

  const handleDeleteChat = async (chat_id: EntityId): Promise<void> => {
    if (!api) return;
    await deleteChat(api, chat_id);
    navigate('/quartermaster/externalAudit');
  };

  if (!chat) {
    return <LoadingMask />;
  }

  return (
    <>
      <Toolbar
        sx={{
          minHeight: '55px !important',
        }}
      >
        <IconButton sx={{ mr: 2 }} onClick={() => navigate(-1)}>
          <ArrowBackOutlinedIcon />
        </IconButton>
        <Typography variant='h6' sx={{ mr: 4 }}>
          {chat.name}
        </Typography>
        <Stack direction={'row'} spacing={2}>
          <Button
            onClick={() =>
              navigate({
                pathname: '/quartermaster/externalAudit/edit',
                search: `?audit_id=${audit_id}`,
              })
            }
            startIcon={<EditOutlinedIcon />}
          >
            Edit
          </Button>
          <Button
            onClick={() => setDeleteOpen(true)}
            startIcon={<DeleteOutlinedIcon />}
          >
            Delete
          </Button>
        </Stack>
      </Toolbar>
      <Divider />

      <Box
        sx={{
          height: 'calc(100vh - 200px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Grid
          container
          spacing={2}
          style={{ padding: '15px', height: '100%', flex: 1 }}
        >
          <Grid size={12}>
            <Alert severity='success'>
              <AlertTitle>
                Interrogating document <b>{chat?.doc_id}</b>
              </AlertTitle>
              Ask questions below
            </Alert>
          </Grid>
          <Grid
            size={12}
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <ChatComponent
              key={chat?.updated_at}
              chat={chat}
              doc_id={chat?.doc_id}
            />
          </Grid>
        </Grid>
      </Box>
      <ChatDeleteDialog
        key={`delete_${chat?.updated_at}`}
        chat={chat}
        open={deleteOpen}
        deleteChat={handleDeleteChat}
        handleClose={() => setDeleteOpen(false)}
      />
    </>
  );
}

export default ExternalAudit;
